import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { AlgebraOperation, Equation } from '../../shared/algebra.ts';
import { buildAlgebraTeachingPlan } from '../lib/algebraTeachingPlan.ts';
import { inkLine, inkFractionEquation, pointAt } from '../lib/inkGlyphs.ts';
import type { NarratedWord } from '../lib/narratedOperation.ts';
import { inkProgress, type TeachingPlan } from '../lib/teachingTimeline.ts';
import { replyAlignment } from '../lib/replyAlignment.ts';
import { paperScrollTop } from '../lib/paperFollow.ts';
import { writtenTarget, noteSentence } from '../lib/writtenTarget.ts';
import './written-board.css';

export type WrittenBoardProps = {
  authoredPlan?: TeachingPlan;
  equation: Equation;
  operation?: AlgebraOperation;
  candidate?: string;
  words?: readonly NarratedWord[];
  positionMs?: number;
  durationMs?: number;
  speaking?: boolean;
  reduced?: boolean;
  label?: string;
  /** Change for each replay/load so manual inspection never disables a new playback. */
  playbackId?: string | number;
  /** Prose help is shown verbatim; it is never interpreted as a solved math step. */
  noteText?: string;
  children?: ReactNode;
};

/** Ink and pencil position depend only on audio time: pause and seek are exact. */
export function WrittenBoard({
  authoredPlan,
  equation,
  operation,
  candidate,
  words,
  positionMs = 0,
  durationMs = 0,
  speaking = false,
  reduced = false,
  label = 'Write it together',
  playbackId,
  noteText,
  children,
}: WrittenBoardProps) {
  const generatedPlan = useMemo(
    () =>
      buildAlgebraTeachingPlan(
        equation,
        operation,
        words,
        durationMs,
        candidate,
      ),
    [
      equation.left.x,
      equation.left.constant,
      equation.right.x,
      equation.right.constant,
      operation?.kind,
      operation?.value,
      candidate,
      words,
      durationMs,
    ],
  );
  const authoredSignature = authoredPlan
    ? JSON.stringify(authoredPlan)
    : undefined;
  const plan = useMemo(
    () => authoredPlan ?? generatedPlan,
    [authoredSignature, generatedPlan],
  );
  const lines = useMemo(
    () =>
      plan.steps.map((step) =>
        step.visual ? inkFractionEquation(step.visual) : inkLine(step.text),
      ),
    [plan],
  );
  const active =
    plan.steps.filter((step) => positionMs >= step.startMs).at(-1) ??
    plan.steps[0];
  const hasAudio = plan.durationMs > 0;
  const rowPositions = useMemo(
    () =>
      lines.reduce<number[]>(
        (positions, _line, index) => [
          ...positions,
          index === 0
            ? 74
            : positions[index - 1] + (lines[index - 1].height > 40 ? 185 : 110),
        ],
        [],
      ),
    [lines],
  );
  const paperRef = useRef<HTMLDivElement>(null);
  const [manualScroll, setManualScroll] = useState(false);
  const previousPosition = useRef(positionMs);
  useEffect(() => setManualScroll(false), [plan, playbackId]);
  useEffect(() => {
    if (positionMs < 100 && previousPosition.current > 250)
      setManualScroll(false);
    previousPosition.current = positionMs;
  }, [positionMs]);
  useLayoutEffect(() => {
    const paper = paperRef.current;
    if (!paper || !hasAudio || manualScroll) return;
    const follow = () => {
      const row = paper.querySelector<SVGGElement>(
        '[data-writing-active="true"]',
      );
      if (!row || paper.clientHeight === 0) return;
      const bounds = paper.getBoundingClientRect(),
        content = row.getBoundingClientRect();
      const next = paperScrollTop(
        paper.scrollTop,
        bounds.top + paper.clientTop,
        paper.clientHeight,
        content.top,
        content.bottom,
      );
      if (next !== paper.scrollTop)
        paper.scrollTo({ top: next, behavior: 'instant' });
    };
    follow();
    const frame = requestAnimationFrame(follow);
    const observer = new ResizeObserver(follow);
    observer.observe(paper);
    const svg = paper.querySelector('svg');
    if (svg) observer.observe(svg);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [active.id, hasAudio, plan, playbackId, manualScroll]);
  const note =
    noteText && !operation && candidate === undefined ? noteText : undefined;
  const noteCue = note
    ? replyAlignment({ text: note, equation, words, positionMs })
    : undefined;
  const noteTarget = note
    ? writtenTarget(equation, noteCue?.target)
    : undefined;
  const lastNoteWord = note
    ? words?.filter((w) => w.startMs <= positionMs).at(-1)
    : undefined;
  const sentencePosition =
    noteCue?.textRange?.start ??
    (note && lastNoteWord
      ? replyAlignment({
          text: note,
          equation,
          words,
          positionMs: lastNoteWord.endMs - 1,
        }).textRange?.start
      : undefined);
  const sentence = note ? noteSentence(note, sentencePosition) : undefined;
  const height = note
    ? 190
    : Math.max(400, rowPositions.at(-1)! + lines.at(-1)!.height * 1.35 + 80);
  const lineLabels: Record<string, string> = {
    original: 'START HERE',
    operation: 'THE SAME CHANGE ON BOTH SIDES',
    factor: 'A COMMON NONZERO FACTOR BECOMES ONE',
    result: 'SIMPLIFY',
    substitution: 'REPLACE EVERY X',
    products: 'MULTIPLY FIRST',
    check: 'COMPARE BOTH SIDES',
  };
  let pencil: readonly [number, number] | undefined;
  return (
    <section
      className={`written-board${note ? ' written-prose-board' : ''}`}
      aria-label={label}
    >
      <div className="written-topline">
        <span>{label}</span>
        {hasAudio && manualScroll ? (
          <button
            className="written-follow"
            aria-label="Resume following narration and show the current step"
            onClick={() => setManualScroll(false)}
          >
            Show current step
          </button>
        ) : (
          <span>
            {speaking ? 'Follow the pencil' : 'Each step stays on the page'}
          </span>
        )}
      </div>
      <div
        className="written-paper-scroll"
        ref={paperRef}
        tabIndex={0}
        role="region"
        aria-label="Written math page. Scroll to inspect each step."
        onWheel={(event) => {
          if (event.deltaY) setManualScroll(true);
        }}
        onTouchMove={() => setManualScroll(true)}
        onKeyDown={(event) => {
          if (
            [
              'ArrowUp',
              'ArrowDown',
              'PageUp',
              'PageDown',
              'Home',
              'End',
              ' ',
            ].includes(event.key)
          )
            setManualScroll(true);
        }}
      >
        <svg
          className="written-paper"
          viewBox={`0 0 860 ${height}`}
          role="img"
          aria-label={plan.steps.map((step) => step.text).join('. ')}
        >
          <title>{plan.steps.map((step) => step.text).join('; ')}</title>
          <defs>
            <pattern
              id="ink-paper-rules"
              width="860"
              height="32"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M0 31.5H860"
                stroke="#cbd7d0"
                strokeWidth=".6"
                opacity=".6"
              />
            </pattern>
          </defs>
          <rect width="860" height={height} rx="18" fill="#fffdf6" />
          <rect
            x="25"
            y="24"
            width="810"
            height={height - 46}
            fill="url(#ink-paper-rules)"
          />
          <path d={`M69 25V${height - 26}`} stroke="#d9b5a3" opacity=".6" />
          {plan.steps.map((step, index) => {
            const line = lines[index];
            const progress =
              hasAudio && !note ? inkProgress(step, positionMs, reduced) : 1;
            const visible = !hasAudio || positionMs >= step.startMs;
            const scale = Math.min(1.35, 700 / line.width);
            const y = rowPositions[index];
            let remaining = line.length * progress;
            return (
              <g
                key={step.id}
                data-writing-step={step.id}
                data-writing-active={step.id === active.id}
                data-math-layout={step.visual?.kind ?? 'line'}
                opacity={visible ? 1 : 0.18}
              >
                <text x="40" y={y + 26} className="written-step-number">
                  {index + 1}
                </text>
                <text x="95" y={y - 17} className="written-line-label">
                  {lineLabels[step.id]}
                </text>
                <g
                  transform={`translate(95 ${y}) scale(${scale})`}
                  fill="none"
                  stroke={index === 1 ? '#8b582c' : '#254d48'}
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {noteTarget && index === 0 && (
                    <g
                      data-spoken-target={noteCue?.target?.id}
                      aria-hidden="true"
                    >
                      <rect
                        x={noteTarget.x - 4}
                        y="-4"
                        width={noteTarget.width + 5}
                        height="47"
                        rx="6"
                        fill="#f7e5ac"
                        stroke="none"
                      />
                      <path
                        d={`M${noteTarget.x + 2} 47H${noteTarget.x + noteTarget.width - 3}`}
                        stroke="#b17a22"
                        strokeWidth="2"
                      />
                      <path
                        d={`M${noteTarget.x + noteTarget.width / 2} 62V53m-4 4 4-4 4 4`}
                        stroke="#8b582c"
                        opacity=".7"
                      />
                    </g>
                  )}
                  {line.strokes.map((stroke, strokeIndex) => {
                    const amount = Math.max(
                      0,
                      Math.min(stroke.length, remaining),
                    );
                    if (
                      visible &&
                      !reduced &&
                      progress > 0 &&
                      progress < 1 &&
                      remaining > 0 &&
                      remaining <= stroke.length
                    ) {
                      const tip = pointAt(stroke.points, amount);
                      pencil = [95 + tip[0] * scale, y + tip[1] * scale];
                    }
                    remaining -= stroke.length;
                    return (
                      <polyline
                        key={strokeIndex}
                        data-ink-role={stroke.role}
                        stroke={
                          stroke.role === 'cancel'
                            ? '#a74f3b'
                            : stroke.role === 'identity'
                              ? '#486b32'
                              : undefined
                        }
                        points={stroke.points.map((p) => p.join(',')).join(' ')}
                        pathLength={stroke.length}
                        strokeDasharray={`${amount} ${stroke.length + 1}`}
                        opacity={amount > 0 ? 1 : 0}
                      />
                    );
                  })}
                </g>
                {index === plan.steps.length - 1 &&
                  index > 0 &&
                  progress === 1 && (
                    <path
                      d={`M95 ${y + 58}Q${95 + (line.width * scale) / 2} ${y + 64} ${95 + line.width * scale} ${y + 57}`}
                      stroke="#9eae78"
                      strokeWidth="3"
                      fill="none"
                      strokeLinecap="round"
                    />
                  )}
              </g>
            );
          })}
          {pencil && !reduced && (
            <g
              className="writing-pencil"
              transform={`translate(${pencil[0]} ${pencil[1]}) rotate(-32)`}
              aria-hidden="true"
              opacity=".72"
            >
              <path d="M0 0L-5-15H5Z" fill="#c69f71" />
              <path d="M0 0L-2-6H2Z" fill="#324942" />
              <rect
                x="-5"
                y="-78"
                width="10"
                height="63"
                rx="2"
                fill="#d5aa52"
                stroke="#8a6a31"
                strokeWidth="1"
              />
              <path d="M-1-76V-17" stroke="#f6dc98" strokeWidth="2" />
              <rect
                x="-5"
                y="-88"
                width="10"
                height="10"
                rx="3"
                fill="#c78676"
              />
            </g>
          )}
        </svg>
      </div>
      {note && sentence && (
        <div className="written-note" aria-label="Tutor note">
          <span className="written-note-label">A note about your equation</span>
          <p>
            {noteCue?.textRange ? (
              <>
                {note.slice(sentence.start, noteCue.textRange.start)}
                <mark>
                  {note.slice(noteCue.textRange.start, noteCue.textRange.end)}
                </mark>
                {note.slice(noteCue.textRange.end, sentence.end)}
              </>
            ) : (
              note.slice(sentence.start, sentence.end)
            )}
          </p>
          <details>
            <summary>Read the full note</summary>
            <p>{note}</p>
          </details>
        </div>
      )}
      <p className="written-caption">
        {note
          ? 'Read the note alongside your current equation.'
          : active.caption}
      </p>
      <details className="written-transcript">
        <summary>Read the written steps</summary>
        <ol>
          {plan.steps.map((step) => (
            <li key={step.id}>
              <strong>{step.text}</strong>
              <span>{step.caption}</span>
            </li>
          ))}
        </ol>
      </details>
      {children}
    </section>
  );
}
